<template>
  <v-container fluid class="pa-0">

    <v-snackbar
        v-model="showSnackbar"
        :color="snackbarColor"
        :timeout="3000"
        location="top"
        rounded="pill"
        elevation="4"
        class="pa-3"
    >
      <div class="d-flex align-center">
        <v-icon class="me-3" :icon="snackbarIcon"></v-icon>
        <span class="text-subtitle-1 font-weight-medium">{{ snackbarText }}</span>
      </div>
      <template v-slot:actions>
        <v-btn
            variant="text"
            icon="mdi-close"
            @click="showSnackbar = false"
        ></v-btn>
      </template>
    </v-snackbar>


    <v-sheet
        color="primary"
        class="py-8 mb-8"
        rounded="0"
        elevation="1"
    >
      <v-row justify="center" class="text-center mt-2">
        <v-icon icon="mdi-clipboard-list" size="56" color="white" class="mb-4"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('interview.interviewPersonas') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">{{ t('interview.createAndManageInterviewScenariosWithAIPoweredPersonas') }}</div>
      </v-row>

    </v-sheet>


    <v-container>
      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-filter-variant" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.selectTopic') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.chooseATopicToGenerateInterviewQuestions') }}</v-card-subtitle>
        </v-card-item>

        <v-card-text>
          <v-row class="align-center">
            <v-col cols="12" md="4">
              <v-select
                  class="domain-select"
                  :label="$t('generic.domain')"
                  :items="domainOptions"
                  item-title="title"
                  item-value="value"
                  v-model="selectedDomain"
                  density="default"
                  variant="outlined"
                  color="primary"
                  prepend-inner-icon="mdi-domain"
                  hide-details
              />
            </v-col>
            <v-col cols="12" md="5">
              <!-- Scoped to just this autocomplete (not global Vuetify config)
                   so the built-in empty-state text ("No data available" etc.)
                   follows the app locale without affecting any other page.
                   Vuetify's own en/ja message bundles are passed explicitly
                   since the root Vuetify config only registers "en". -->
              <v-locale-provider :locale="$i18n.locale" :messages="{ en: vuetifyEn, ja: vuetifyJa }">
              <v-autocomplete
                  v-show="!isCampaignsLoading"
                  v-model="selectedCampaign"
                  :items="dropdownCampaigns"
                  :label="$t('interview.selectATopic')"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                  class="rounded-lg"
              >
                <template v-slot:prepend-item>
                  <v-list-item prepend-icon="mdi-filter-variant" :title="$t('interview.topics')"></v-list-item>
                  <v-divider></v-divider>
                </template>
              </v-autocomplete>
              </v-locale-provider>

              <div v-show="isCampaignsLoading" class="text-center mt-4">
                <v-progress-circular indeterminate color="primary" class="mb-2" />
                <p class="text-body-2 text-disabled">{{ t('interview.loadingTopics') }}</p>
              </div>
            </v-col>

            <v-col cols="12" md="3">
              <v-row no-gutters class="ga-2">
                <v-col>
                  <v-btn
                      @click="openDialog"
                      color="secondary"
                      class="py-2"
                      size="large"
                      rounded="pill"
                      elevation="1"
                      v-tooltip.bottom="$t('interview.addANewTopicToTheList')"
                      block
                      variant="tonal"
                  >
                    <v-icon start icon="mdi-plus-circle-outline" class="me-1"></v-icon>
                    {{ t('interview.newTopic') }}
                  </v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                      @click="promptDeleteTopic"
                      color="error"
                      size="large"
                      rounded="pill"
                      elevation="1"
                      variant="tonal"
                      :disabled="!selectedCampaign"
                      v-tooltip.bottom="$t('interview.deleteSelectedTopic')"
                      icon="mdi-delete-outline"
                  ></v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Uploads are ignored by the template endpoint, so the card is hidden there -->
      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1" v-if="executionMode !== 'template_based'">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-paperclip" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.topicAttachments') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.manageUploadedFiles') }}</v-card-subtitle>
        </v-card-item> <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <file-upload
                :extensions="fu_config.extensions"
                :accept="fu_config.accept"
                :multiple="fu_config.multiple"
                v-model="uploaded_files"
                ref="image_uploader"
            >
              <v-btn
                  color="primary"
                  size="large"
                  rounded="pill"
                  elevation="1"
                  block
                  variant="tonal"
              >
                <v-icon start icon="mdi-plus-circle-outline" class="me-1"></v-icon>
                {{ t('interview.addFiles') }}
              </v-btn>
            </file-upload>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
                @click="removeUploadedFiles"
                rounded
                color="red"
                size="large"
                elevation="1"
                block
                :disabled="uploaded_files.length === 0"
            >
              <v-icon icon="mdi-broom" class="me-1"></v-icon>
              {{ t('generic.clear') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="uploaded_files.length > 0">
          <v-col cols="12">
            <v-card rounded="lg" variant="outlined" elevation="0">
              <v-card
                  v-for="(file, index) in uploaded_files"
                  :key="index"
                  :class="(index === uploaded_files.length - 1) ? 'ml-2 mr-2 mt-4 mb-4' : 'ml-2 mr-2 mt-4'"
                  variant="elevated">
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon :icon="allowed_image_ext.includes(getFileExt(file.name)) ? 'mdi-image-outline' : 'mdi-file-document-outline'" color="primary" size="20"></v-icon>
                  </template>
                  <v-card-title class="text-subtitle-2 ps-1">{{ file.name }}</v-card-title>
                  <template v-slot:append>
                    <v-btn size="small" v-if="!downloading_spinner" style="background-color: white" icon="mdi-download" @click="downloadMedia(file.file, file.name)"></v-btn>
                    <v-progress-circular v-else indeterminate size="40"></v-progress-circular>
                    <v-btn size="small" style="background-color: white" icon="mdi-delete" class="ml-2" @click="removeFileFromUploadedFiles(index)"></v-btn>
                  </template>
                </v-card-item>
              </v-card>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      </v-card>


      <!-- Execution Mode -->
      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-cog-outline" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.executionMode') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.chooseHowTheSessionWillBeExecuted') }}</v-card-subtitle>
        </v-card-item>
        <v-card-text>
          <v-radio-group v-model="executionMode" hide-details>
            <div class="d-flex" style="gap: 10px; width: 100%;">
              <v-card
                :variant="executionMode === 'interview' ? 'tonal' : 'outlined'"
                :color="executionMode === 'interview' ? 'primary' : undefined"
                rounded="lg"
                class="cursor-pointer flex-grow-1"
                style="flex-basis: 0;"
                @click="executionMode = 'interview'"
              >
                <div class="d-flex align-start ga-3" style="padding: 30px 30px 30px 30px;">
                  <v-radio value="interview" color="primary" density="compact" hide-details class="mt-1 flex-shrink-0" />
                  <div style="text-align: left; width: 100%;">
                    <div class="d-flex align-center ga-2 mb-1" style="justify-content: flex-start;">
                      <v-icon icon="mdi-account-voice" color="primary" size="32" />
                      <span class="text-subtitle-1 font-weight-bold">{{ t('interview.executionModeInterviewTitle') }}</span>
                    </div>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      {{ t('interview.executionModeInterviewDescription') }}
                    </p>
                  </div>
                </div>
              </v-card>
              <v-card
                :variant="executionMode === 'question_driven' ? 'tonal' : 'outlined'"
                :color="executionMode === 'question_driven' ? 'primary' : undefined"
                rounded="lg"
                class="cursor-pointer flex-grow-1"
                style="flex-basis: 0;"
                @click="executionMode = 'question_driven'"
              >
                <div class="d-flex align-start ga-3" style="padding: 30px 30px 30px 30px;">
                  <v-radio value="question_driven" color="primary" density="compact" hide-details class="mt-1 flex-shrink-0" />
                  <div style="text-align: left; width: 100%;">
                    <div class="d-flex align-center ga-2 mb-1" style="justify-content: flex-start;">
                      <v-icon icon="mdi-account-group" color="primary" size="32" />
                      <span class="text-subtitle-1 font-weight-bold">{{ t('interview.executionModeQuestionDrivenTitle') }}</span>
                    </div>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      {{ t('interview.executionModeQuestionDrivenDescription') }}
                    </p>
                  </div>
                </div>
              </v-card>
              <v-card
                :variant="executionMode === 'template_based' ? 'tonal' : 'outlined'"
                :color="executionMode === 'template_based' ? 'primary' : undefined"
                rounded="lg"
                class="cursor-pointer flex-grow-1"
                style="flex-basis: 0;"
                @click="executionMode = 'template_based'"
              >
                <div class="d-flex align-start ga-3" style="padding: 30px 30px 30px 30px;">
                  <v-radio value="template_based" color="primary" density="compact" hide-details class="mt-1 flex-shrink-0" />
                  <div style="text-align: left; width: 100%;">
                    <div class="d-flex align-center ga-2 mb-1" style="justify-content: flex-start;">
                      <v-icon icon="mdi-account-group" color="primary" size="32" />
                      <span class="text-subtitle-1 font-weight-bold">{{ t('interview.executionModeTemplateBasedTitle') }}</span>
                    </div>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      {{ t('interview.executionModeTemplateBasedDescription') }}
                    </p>
                  </div>
                </div>
              </v-card>
            </div>
          </v-radio-group>
        </v-card-text>
      </v-card>

      <!-- Interview Prompt Configuration -->
      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1" v-if="executionMode === 'interview'">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-tune-variant" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.aiPromptConfiguration') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.selectATailoredPromptForGeneratingInterviewQuestions') }}</v-card-subtitle>
        </v-card-item>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.generateQuestionPrompt') }}</div>
              <p class="text-body-2 text-medium-emphasis mb-3">
                {{ t('interview.generateQuestionPromptDescription') }}
              </p>
              <v-select
                  v-model="selectedQuestionGenerationPrompt"
                  :items="questionGenerationPrompts"
                  item-title="name"
                  item-value="prompt_id"
                  variant="outlined"
                  density="comfortable"
                  :placeholder="t('interview.selectAPrompt')"
                  color="primary"
                  hide-details
                  class="ai-prompt-select"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- AI Prompt Configuration -->
      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1" v-if="executionMode === 'question_driven'">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-tune-variant" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.aiPromptConfiguration') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.selectTailoredPromptsForQuestionsAndInsights') }}</v-card-subtitle>
        </v-card-item>
        <v-card-text>
          <div class="ai-prompt-grid">
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.generateQuestionPrompt') }}</div>
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.summarizePrompt') }}</div>
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.improvementPlanPrompt') }}</div>

            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ t('interview.generateQuestionPromptDescription') }}
            </p>
            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ t('interview.summarizePromptDescription') }}
            </p>
            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ t('interview.improvementPlanPromptDescription') }}
            </p>

            <v-select
                v-model="selectedQuestionGenerationPrompt"
                :items="questionGenerationPrompts"
                item-title="name"
                item-value="prompt_id"
                variant="outlined"
                density="comfortable"
                :placeholder="t('interview.selectAPrompt')"
                color="primary"
                hide-details
                class="ai-prompt-select"
            />
            <v-select
                v-model="selectedSummarizePrompt"
                :items="summarizePrompts"
                item-title="name"
                item-value="prompt_id"
                variant="outlined"
                density="comfortable"
                :placeholder="t('interview.selectAPrompt')"
                color="primary"
                hide-details
                class="ai-prompt-select"
            />
            <v-select
                v-model="selectedImprovementPlanPrompt"
                :items="improvementPlanPrompts"
                item-title="name"
                item-value="prompt_id"
                variant="outlined"
                density="comfortable"
                :placeholder="t('interview.selectAPrompt')"
                color="primary"
                hide-details
                class="ai-prompt-select"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- Template Configuration -->
      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1" v-if="executionMode === 'template_based'">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-tune-variant" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.templateConfiguration') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.templateConfigurationSubtitle') }}</v-card-subtitle>
        </v-card-item>
        <v-card-text>
          <div class="ai-prompt-grid">
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.meetingTemplate') }}</div>
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.summarizePrompt') }}</div>
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ t('interview.improvementPlanPrompt') }}</div>

            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ t('interview.meetingTemplateDescription') }}
            </p>
            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ t('interview.summarizePromptDescription') }}
            </p>
            <p class="text-body-2 text-medium-emphasis mb-3">
              {{ t('interview.improvementPlanPromptDescription') }}
            </p>

            <v-select
                v-model="selectedMeetingTemplate"
                :items="meetingTemplates"
                item-title="template_name"
                item-value="template_id"
                variant="outlined"
                density="comfortable"
                :placeholder="t('interview.selectAMeetingTemplate')"
                color="primary"
                hide-details
                class="ai-prompt-select"
            />
            <v-select
                v-model="selectedTemplateSummarizePrompt"
                :items="templateSummarizePrompts"
                item-title="name"
                item-value="prompt_id"
                variant="outlined"
                density="comfortable"
                :placeholder="t('interview.selectAPrompt')"
                color="primary"
                hide-details
                class="ai-prompt-select"
            />
            <v-select
                v-model="selectedTemplateImprovementPlanPrompt"
                :items="templateImprovementPlanPrompts"
                item-title="name"
                item-value="prompt_id"
                variant="outlined"
                density="comfortable"
                :placeholder="t('interview.selectAPrompt')"
                color="primary"
                hide-details
                class="ai-prompt-select"
            />
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Stimulus materials: each file is uploaded as soon as it is added, for
               the selected template's domain; the start request only references
               them by file_id, in the order of this list. -->
          <div class="text-subtitle-1 font-weight-bold mb-1">
            {{ t('interview.stimulusMaterials') }}
            <span class="text-body-2 text-medium-emphasis">{{ t('promptmanagementtemplates.optionalLabel') }}</span>
          </div>
          <p class="text-body-2 text-medium-emphasis mb-3">
            {{ t('interview.stimulusMaterialsDescription', { maxMb: maxUploadMb, maxPages: maxAttachmentPages }) }}
          </p>

          <input
              ref="attachmentInput"
              type="file"
              class="d-none"
              multiple
              :accept="attachmentAccept"
              @change="onAttachmentsPicked"
          >

          <div class="d-flex align-center flex-wrap ga-3 mb-3">
            <v-btn
                color="primary"
                variant="tonal"
                rounded="pill"
                prepend-icon="mdi-paperclip"
                :disabled="!canAddStimulusMaterials"
                @click="pickAttachments"
            >
              {{ t('interview.addStimulusMaterials') }}
            </v-btn>
            <v-chip
                v-if="selectedTemplate"
                :color="attachmentPagesOverLimit ? 'error' : 'primary'"
                variant="tonal"
                prepend-icon="mdi-file-multiple-outline"
            >
              {{ t('interview.stimulusMaterialsPageCount', { count: attachmentPages, max: maxAttachmentPages }) }}
            </v-chip>
          </div>

          <attachment-upload-list
              v-if="templateAttachments.length"
              v-model:items="templateAttachments"
              :highlighted-file-ids="attachmentStartError?.fileIds"
              :highlight-detail="attachmentStartError?.detail"
              @remove="removeTemplateAttachment"
              @retry="uploadTemplateAttachment"
          />

          <div v-if="!selectedTemplate" class="text-caption text-medium-emphasis">
            {{ t('interview.selectATemplateToAddStimulusMaterials') }}
          </div>
          <v-alert
              v-else-if="!selectedTemplateShowsAttachment"
              :type="templateAttachments.length ? 'warning' : 'info'"
              variant="tonal"
              density="compact"
              class="mt-1"
          >
            {{ templateAttachments.length
                ? t('interview.templateHidesAddedStimulusMaterials')
                : t('interview.templateDoesNotShowStimulusMaterials') }}
          </v-alert>

          <v-alert v-if="attachmentPagesOverLimit" type="error" variant="tonal" density="compact" class="mt-1">
            {{ t('interview.stimulusMaterialsOverLimit', { count: attachmentPages, max: maxAttachmentPages }) }}
          </v-alert>
          <v-alert
              v-if="attachmentNotice"
              type="warning"
              variant="tonal"
              density="compact"
              closable
              class="mt-3"
              @click:close="attachmentNotice = ''"
          >
            {{ attachmentNotice }}
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1" v-if="executionMode !== 'template_based' && editableQuestions.length > 0">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-file-question-outline" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('interview.generatedQuestions') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.questionsAboutTheSelectedTopicThatWillBeAskedDuringTheInterviewOrFocusGroup') }}</v-card-subtitle>
        </v-card-item>

        <!-- On-demand fallback only: this shows if bilingual prep failed for
             one language and the user later switches to it, triggering a
             retry. In the normal (success) case both languages are already
             cached before the questions above are ever shown, so this never
             fires on a routine switch. -->
        <v-progress-linear
            v-if="isTranslatingCurrentView"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-card-text v-if="isTranslatingCurrentView" class="pb-0 pt-2 text-caption text-medium-emphasis">
          {{ t('interview.translatingQuestions') }}
        </v-card-text>

        <v-card-text>
          <v-container>
            <v-row v-for="(question, index) in editableQuestions" :key="index">
              <v-col cols="12">
                <v-card variant="outlined" rounded="lg" class="pa-3">
                  <div class="d-flex justify-space-between align-center">
                    <div class="text-subtitle-1 font-weight-medium text-truncate">
                      {{ index + 1 }}. {{ question.text }}
                    </div>
                    <v-btn
                        :icon="question.isEditing ? 'mdi-close' : 'mdi-pencil'"
                        size="small"
                        color="primary"
                        variant="text"
                        @click="question.isEditing = !question.isEditing"
                    ></v-btn>
                  </div>

                  <v-expand-transition>
                    <div v-if="question.isEditing" class="mt-3">
                      <v-textarea
                          v-model="question.text"
                          :label="$t('interview.editQuestionText')"
                          variant="outlined"
                          rows="3"
                          auto-grow
                      ></v-textarea>

                      <div class="text-subtitle-2 mb-2">
                        {{ question.file_id 
                          ? $t('interview.associatedFile') + ' ' + question.file_id 
                          : $t('interview.noFileAssociated') 
                        }}
                      </div>
                      <v-btn
                          color="success"
                          variant="elevated"
                          rounded="pill"
                          @click="question.isEditing = false">
                        <v-icon start>mdi-content-save</v-icon>
                        {{ t('interview.saveChanges') }}
                      </v-btn>
                    </div>
                  </v-expand-transition>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <v-btn
                    @click="confirmQuestions"
                    color="primary"
                    size="large"
                    rounded="pill"
                    elevation="2"
                    block
                    :disabled="questionsConfirmed"
                >
                  <v-icon start>mdi-check-circle</v-icon>
                  {{ questionsConfirmed ? $t('interview.questionsConfirmed') : $t('interview.confirmQuestions') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card rounded="lg" class="mb-6" variant="outlined" elevation="1">
        <v-card-item>
          <template v-slot:prepend>
            <v-icon icon="mdi-lightning-bolt" color="primary" size="24"></v-icon>
          </template>
          <v-card-title class="text-h6 ps-1">{{ t('generic.actions') }}</v-card-title>
          <v-card-subtitle class="ps-1">{{ t('interview.generateQuestionsOrStartInterviews') }}</v-card-subtitle>
        </v-card-item>

        <v-card-text>
          <v-alert
              v-if="backgroundRunStatus === 'running'"
              type="info"
              variant="tonal"
              class="mb-4"
              icon="mdi-sync"
          >
            <div class="d-flex align-center">
              <v-progress-circular indeterminate size="18" width="2" class="me-3"></v-progress-circular>
              {{ t('interview.aBackgroundRunIsInProgressStartButtonsAreDisabledUntilItCompletes') }}
            </div>
          </v-alert>

          <v-row class="align-center" justify="space-between">
            <v-col cols="12" sm="6" class="my-2">
              <v-btn
                  :loading="isLoading_questions"
                  :disabled="!selectedCampaign || isLoading_questions || executionMode === 'template_based' || !selectedQuestionGenerationPrompt"
                  @click="generateQuestions"
                  color="primary"
                  class="py-2"
                  size="large"
                  rounded="pill"
                  elevation="1"
                  v-tooltip.bottom="$t('interview.generateQuestionsForTheSelectedTopic')"
                  block
              >
                <v-icon start class="me-2">mdi-account-question-outline</v-icon>
                {{ t('interview.generateQuestions') }}
              </v-btn>
            </v-col>

            <v-col cols="12" sm="6" class="my-2" v-if="executionMode === 'interview'">
              <v-btn
                  :loading="isLoading_interview"
                  :disabled="backgroundRunStatus === 'running'"
                  @click="startInterview"
                  color="success"
                  class="py-2"
                  size="large"
                  rounded="pill"
                  elevation="1"
                  v-tooltip.bottom="$t('interview.interviewTheSelectedPersonas')"
                  block
              >
                <v-icon start class="me-2">mdi-account-voice</v-icon>
                {{ t('interview.startInterview') }}
              </v-btn>
            </v-col>

            <v-col cols="12" sm="6" class="my-2" v-else>
              <v-btn
                  :loading="isLoading_fg"
                  :disabled="backgroundRunStatus === 'running' || !!templateAttachmentsBlocker"
                  @click="startFocusGroup"
                  color="info"
                  class="py-2"
                  size="large"
                  rounded="pill"
                  elevation="1"
                  v-tooltip.bottom="$t('interview.runAFocusGroupWithTheSelectedPersonas')"
                  block
              >
                <v-icon start class="me-2">mdi-account-group</v-icon>
                {{ t('interview.startFocusGroup') }}
              </v-btn>
              <!-- A disabled button shows no tooltip: say why here. -->
              <div v-if="templateAttachmentsBlocker" class="text-caption text-error text-center mt-2">
                {{ templateAttachmentsBlockerMessage }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="12" md="8">
          <v-card rounded="lg" class="h-100" elevation="1">
            <v-card-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-account-multiple-outline" color="primary" size="24"></v-icon>
              </template>
              <v-card-title class="text-h6 ps-1">{{ t('interview.choosePersonas') }}</v-card-title>
              <v-card-subtitle class="ps-1">{{ t('interview.selectPersonasToIncludeInTheInterviewProcess') }}</v-card-subtitle>
            </v-card-item>

            <v-card-text style="max-height: 350px; overflow-y: auto;" class="pt-0">
              <div class="d-flex flex-column align-end mb-2 ga-2">
                <v-btn
                    @click="filterDialogOpen = true"
                    prepend-icon="mdi-filter-variant"
                    variant="tonal"
                    color="primary"
                    size="small"
                    class="text-none"
                >
                  {{ t('personagrid.filterButton') }}
                  <v-badge
                      v-if="activeFilterCount"
                      :content="activeFilterCount"
                      color="error"
                      inline
                      class="ms-2"
                  />
                </v-btn>
                <v-btn
                    size="small"
                    @click="toggleCheckAll"
                    :color="allSelected ? 'error' : 'secondary'"
                    class="text-none"
                    variant="text"
                >
                  <v-icon start size="small" class="me-1">
                    {{ allSelected ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline' }}
                  </v-icon>

                  {{ allSelected 
                      ? $t('interview.deselectAll') 
                      : $t('interview.selectAll') 
                  }}
                </v-btn>
              </div>
              <v-chip-group
                  v-model="selectedCheckboxes"
                  column
                  multiple
                  class="persona-chips"
              >
                <v-chip
                    v-for="(item, index) in filteredPersonaOptions"
                    :key="item.persona_id || index"
                    :value="item"
                    color="primary"
                    variant="outlined"
                    class="mb-3 pa-2 rounded-lg"
                    size="large"
                    filter
                >
                  <v-avatar start class="me-2 ml-1">
                    <v-img
                        :src="`https://randomuser.me/api/portraits/lego/${(index % 8) + 1}.jpg`"></v-img>
                  </v-avatar>
                  {{ item.name }}
                </v-chip>
              </v-chip-group>

              <v-alert
                  v-if="showNoFilterResults"
                  type="info"
                  variant="tonal"
                  class="mt-4"
              >
                {{ t('personagrid.noPersonasMatchFilters') }}
              </v-alert>

              <v-alert
                  v-else-if="personaOptions.length === 0"
                  type="info"
                  variant="tonal"
                  class="mt-4"
              >
                {{ t('interview.noPersonasAvailablePleaseCheckYourConfiguration') }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card rounded="lg" class="h-100" elevation="1">
            <v-card-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-text-box-outline" color="primary" size="24"></v-icon>
              </template>
              <v-card-title class="text-h6 ps-1">{{ t('interview.topicDescription') }}</v-card-title>
              <v-card-subtitle class="ps-1">{{ t('interview.detailsAboutTheSelectedTopic') }}</v-card-subtitle>
            </v-card-item>

            <v-card-text class="pt-0">
              <v-alert
                  v-if="!selectedCampaign"
                  type="info"
                  variant="tonal"
              >
                {{ t('interview.pleaseSelectATopicToViewItsDescription') }}
              </v-alert>
              <div v-else class="topic-description white-space-pre-wrap">
                {{ currentCampaignDescription }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </v-container>

    <FilterSettingsDialog
        v-model="filterDialogOpen"
        :domain="selectedDomain"
        :personas="staticPersonasForFilter"
        :generated-personas="generatedPersonasForFilter"
        :chat-api-personas="personaOptions"
        :is-generated="false"
        :applied-criteria="activeFilterCriteria"
        :schema-attributes="schemaAttributes"
        @apply="onApplyFilters"
        @clear="onClearFilters"
    />

    <CampaignDialog
        v-model:showDialog="isAddDialogVisible"
        @submit="addCampaign"
        :selectedDomain="domainOptions.find(option => option.value === selectedDomain)?.title"
    />

    <v-dialog v-model="showDeleteTopicConfirm" max-width="400">
      <v-card class="rounded-xl elevation-5">
        <v-card-title class="text-h5 bg-error text-white rounded-t-xl py-4">
          <v-icon icon="mdi-delete-alert" class="me-2"></v-icon>
          {{ t('interview.deleteTopic') }}
        </v-card-title>
        <v-card-text class="pa-6 text-body-1">
          {{ t('interview.areYouSureYouWantToDeleteThisActionThisActionCannotBeUndone') }}: <strong>{{ topicToDelete?.topic_name }}</strong>
        </v-card-text>
        <v-card-actions class="pa-4 pb-6">
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" @click="showDeleteTopicConfirm = false" rounded="pill">
            {{ t('generic.cancel') }}
          </v-btn>
          <v-btn color="error" variant="elevated" @click="confirmDeleteTopic" rounded="pill" :loading="deletingTopic">
            <v-icon start icon="mdi-delete" class="me-1"></v-icon>
            {{ t('generic.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  <not-supported-snack :visible="snackbar_not_supported"></not-supported-snack>
  <downloaded-snack :visible="snackbar_downloaded"></downloaded-snack>
</template>

<script>
import {
  start_interview,
  fetch_campaigns,
  add_campaign,
  delete_campaign,
  fetch_persona_names,
  start_focus_group,
  start_template_focus_group,
  generate_interview_questions,
  translate_interview_questions,
  fetch_focus_groups,
  get_background_run_status,
  build_domain_options,
  get_default_domain,
  fetch_prompts,
  get_current_user_from_storage,
  fetch_meeting_templates,
  upload_file,
} from "../api.js";
import CampaignDialog from "./InterviewCampaignDialog.vue";
import AttachmentUploadList from "./AttachmentUploadList.vue";
import FilterSettingsDialog from '@/components/FilterSettingsDialog.vue';
import { templateShowsAttachment } from '@/utils/meetingTemplateSteps';
import {
  MAX_ATTACHMENT_PAGES,
  MAX_UPLOAD_MB,
  attachmentPageTotal,
  attachmentsDomainMismatch,
  attachmentsNamedIn,
  attachmentsStartBlocker,
  buildTemplateFocusGroupPayload,
  localFileSignature,
  uploadListedAttachment,
  uploadingAttachment,
} from '@/utils/templateAttachment';
import {
  clearActiveFilterState,
  loadActiveFilterState,
  saveActiveFilterState,
} from '@/utils/filterStorage';
import {
  loadDomainPersonasWithOverlay,
  loadSavedPersonasForDomain,
  loadDomainSchemaAttributes,
} from '@/utils/personaDomainLoader';
import {
  filterChatPersonaOptions,
  getAllFilterFields,
  hasActiveCriteria,
  reconcileFilterCriteria,
  formatDroppedFilterFieldsMessage,
} from '@/utils/personaFilter';
import FileUpload from "vue-upload-component";
import {ref} from "vue";
import DownloadedSnack from "@/components/DownloadedSnack.vue";
import NotSupportedSnack from "@/components/NotSupportedSnack.vue";
import { useI18n } from "vue-i18n";
import { en as vuetifyEn, ja as vuetifyJa } from 'vuetify/locale';

export default {
  components: {
    NotSupportedSnack, DownloadedSnack,
    FileUpload,
    CampaignDialog,
    AttachmentUploadList,
    FilterSettingsDialog,
  },
  setup() {
      const { t } = useI18n();

      return {
      t
      };
  },
  data() {
    return {
      // Vuetify's own locale message bundles, passed explicitly to the
      // page-scoped v-locale-provider below (the root Vuetify config only
      // registers "en", so "ja" must be supplied here for the topic
      // autocomplete's built-in empty-state text to localize).
      vuetifyEn,
      vuetifyJa,
      campaigns: [],
      selectedCampaign: null,
      selectedCheckboxes: [],
      personaOptions: [],
      filterDialogOpen: false,
      activeFilterCriteria: [],
      staticPersonasForFilter: [],
      generatedPersonasForFilter: [],
      schemaAttributes: [],
      isAddDialogVisible: false,
      isLoading_interview: false,
      isLoading_fg: false,
      isCampaignsLoading: false,
      backgroundRunStatus: 'idle',
      backgroundRunError: null,
      pollingInterval: null,
      generated_questions: '',

      // questions section
      isEditing: false,
      isLoading_questions: false,
      editableQuestions: [],
      questionsConfirmed: false,
      questionFileMappingFromServer: {},
      // Bilingual question display: { en: [...], ja: [...] }, keyed by the
      // same shape as editableQuestions. Populated with the generation
      // language on generate, and lazily filled in for the other language
      // via on-demand translation when the UI language switches.
      questionsByLanguage: {},
      isTranslatingQuestions: false,
      translatingToLanguage: null,
      // Bumped on every fresh generation (and on topic change, which also
      // clears editableQuestions). A translation in flight captures this at
      // start and re-checks it after the await -- if it changed, a newer
      // generation has since replaced the question set it was translating,
      // so the (now stale) result is discarded instead of being cached.
      questionsGenerationToken: 0,

      // Toast notification
      showSnackbar: false,
      snackbarText: "",
      snackbarColor: "error",
      snackbarIcon: "mdi-alert-circle",

      // Image Uploader
      fu_config: {
        extensions: '.jpeg,.png,.jpg,.pptx,.ppt,.pdf,.docx',
        accept: '.jpeg,.png,.jpg,.pptx,.ppt,.pdf,.docx',
        multiple: true,
      },
      uploaded_files: ref([]),
      uploaded_image_url: null,
      allowed_image_ext: ['.jpeg', '.png', '.jpg'],
      allowed_document_ext: ['.pdf','.docx','.ppt','.pptx'],
      // SNACKBAR
      snackbar_not_supported: false,
      snackbar_downloaded: true,
      //SPINNER
      downloading_spinner: false,
      // Delete topic
      showDeleteTopicConfirm: false,
      topicToDelete: null,
      deletingTopic: false,

      // Execution Mode
      executionMode: 'question_driven',

      // AI Prompt Configuration
      aiPrompts: [],
      isLoadingAIPrompts: false,
      selectedQuestionGenerationPrompt: null,
      selectedSummarizePrompt: null,
      selectedImprovementPlanPrompt: null,

      // Template Configuration
      meetingTemplates: [],
      isLoadingMeetingTemplates: false,
      selectedMeetingTemplate: null,
      templatePrompts: [],
      isLoadingTemplatePrompts: false,
      selectedTemplateSummarizePrompt: null,
      selectedTemplateImprovementPlanPrompt: null,

      // Stimulus materials of the template run, in the order the group sees
      // them: one item per file, uploaded once when added (see
      // utils/templateAttachment.js), plus the AbortController of its upload.
      templateAttachments: [],
      attachmentKeySeq: 0,
      // The last start error naming listed files: { fileIds, detail }.
      attachmentStartError: null,
      attachmentNotice: '',
      // Only narrows the file picker: the backend checks types, size and pages.
      attachmentAccept: '.png,.jpg,.jpeg,.webp,.gif,.pdf,.pptx',
      maxAttachmentPages: MAX_ATTACHMENT_PAGES,
      maxUploadMb: MAX_UPLOAD_MB,

      // DOMAINS
      selectedDomain: get_default_domain(),
    };
  },
  computed: {
    // True only when the translation in flight is for the language the user
    // is CURRENTLY looking at (i.e. they switched to a not-yet-ready one and
    // are waiting on it) -- not when it's the silent background prep of the
    // other language kicked off right after generation, which the user has
    // no reason to see a loading state for (their current view is already
    // fully ready).
    isTranslatingCurrentView() {
      return this.isTranslatingQuestions && this.translatingToLanguage === this.resolveCurrentLanguage();
    },
domainOptions() {
      return build_domain_options(this.$t);
    },
    questionGenerationPrompts() {
      return this.aiPrompts.filter(p => p.prompt_type === 'question_generation');
    },
    summarizePrompts() {
      return this.aiPrompts.filter(p => p.prompt_type === 'summarise');
    },
    improvementPlanPrompts() {
      return this.aiPrompts.filter(p => p.prompt_type === 'improvement_plan');
    },
    templateSummarizePrompts() {
      return this.templatePrompts.filter(p => p.prompt_type === 'summarise');
    },
    templateImprovementPlanPrompts() {
      return this.templatePrompts.filter(p => p.prompt_type === 'improvement_plan');
    },
    selectedTemplate() {
      return this.meetingTemplates.find(t => t.template_id === this.selectedMeetingTemplate) ?? null;
    },
    selectedTemplateShowsAttachment() {
      return templateShowsAttachment(this.selectedTemplate);
    },
    // Materials are uploaded for the selected template's domain, and only for a
    // template with a step that shows them (the backend refuses them otherwise).
    canAddStimulusMaterials() {
      return !!this.selectedTemplate && this.selectedTemplateShowsAttachment;
    },
    attachmentPages() {
      return attachmentPageTotal(this.templateAttachments);
    },
    attachmentPagesOverLimit() {
      return this.attachmentPages > MAX_ATTACHMENT_PAGES;
    },
    // Why the template run cannot start with the listed materials, or null.
    templateAttachmentsBlocker() {
      if (this.executionMode !== 'template_based') return null;
      return attachmentsStartBlocker(this.templateAttachments, this.selectedTemplate);
    },
    templateAttachmentsBlockerMessage() {
      const keys = {
        error: 'interview.startBlockedByFailedStimulusMaterials',
        over_limit: 'interview.startBlockedByStimulusMaterialPages',
        not_shown: 'interview.startBlockedByHiddenStimulusMaterials',
        uploading: 'interview.startBlockedByStimulusMaterialUploads',
      };
      const key = keys[this.templateAttachmentsBlocker];
      return key ? this.$t(key, { max: MAX_ATTACHMENT_PAGES }) : '';
    },
    dropdownCampaigns() {
      return this.campaigns.map((campaign) => campaign.topic_name);
    },
    filteredPersonaOptions() {
      return filterChatPersonaOptions(this.personaOptions, {
        staticPersonas: this.staticPersonasForFilter,
        generatedPersonas: this.generatedPersonasForFilter,
        criteria: this.activeFilterCriteria,
        schemaAttributes: this.schemaAttributes,
      });
    },
    currentFilterFields() {
      return getAllFilterFields(this.staticPersonasForFilter, false, this.schemaAttributes);
    },
    activeFilterCount() {
      return this.activeFilterCriteria.length;
    },
    showNoFilterResults() {
      if (!hasActiveCriteria(this.activeFilterCriteria, this.currentFilterFields)) {
        return false;
      }
      return this.personaOptions.length > 0 && this.filteredPersonaOptions.length === 0;
    },
    currentCampaignDescription() {
      const campaign = this.campaigns.find(campaign => campaign.topic_name === this.selectedCampaign);
      return campaign ? campaign.description : '';
    },
    allSelected() {
      return (
        this.filteredPersonaOptions.length > 0
        && this.selectedCheckboxes.length === this.filteredPersonaOptions.length
      );
    },
    canStartInterview() {
      return (
          this.selectedCheckboxes.length > 0 &&
          this.selectedCampaign &&
          this.editableQuestions.length > 0 &&
          this.questionsConfirmed
      );
    },
    getTooltipMessage() {
      if (!this.questionsConfirmed) {
        return 'Please confirm questions first';
      }
      if (!this.selectedCampaign) {
        return 'Please select a topic first';
      }
      if (this.selectedCheckboxes.length === 0) {
        return 'Please select at least one persona';
      }
      if (this.editableQuestions.length === 0) {
        return 'Please generate questions first';
      }
      return 'Start the interview or focus group';
    }
  },
  watch: {
    selectedDomain(newValue, oldValue){
      if(newValue !== oldValue){
        if (attachmentsDomainMismatch(this.templateAttachments, { domain_id: newValue })) {
          this.discardTemplateAttachments();
        }
        this.selectedCheckboxes = [];
        this.loadFilterPersonaSources(newValue);
        this.fetchCampaigns(newValue);
        this.fetchPersonaNames(newValue);
        this.fetchAIPrompts();
        this.fetchMeetingTemplates();
        this.fetchTemplatePrompts();
        this.checkBackgroundRunStatus();
      }
    },
    activeFilterCriteria: {
      deep: true,
      handler() {
        this.ensureSelectedPersonasStillVisible();
      },
    },
    selectedCampaign(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.editableQuestions = [];
        this.questionsByLanguage = {};
        this.questionsGenerationToken += 1;
        this.questionsConfirmed = false;
      }
    },
    // The materials were uploaded for one domain: a template of another domain
    // cannot take them, so the list (uploads in flight included) is emptied.
    // A template hiding them in every step only blocks the start: the user may
    // switch back, or remove the files.
    selectedTemplate(template) {
      if (attachmentsDomainMismatch(this.templateAttachments, template)) {
        this.discardTemplateAttachments();
      }
    },
    executionMode(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue === 'question_driven') {
          this.fetchAIPrompts();
        } else if (newValue === 'template_based') {
          this.fetchMeetingTemplates();
          this.fetchTemplatePrompts();
        }
      }
    },
    // Instantly re-display already-generated questions in the newly selected
    // UI language (translating on demand once, then cached) -- mirrors the
    // Focus Group Analysis bilingual-display UX. Does not trigger on mount.
    '$i18n.locale'(newLocale, oldLocale) {
      if (newLocale === oldLocale) return;
      this.syncQuestionsToLanguage(this.resolveCurrentLanguage());
    },
    uploaded_files(newVal){
      if(newVal.length > 0){
        newVal.forEach((file) => {
          const ext = this.getFileExt(file.name);
          if(this.fu_config.accept.includes(ext)){
            this.uploaded_image_url = URL.createObjectURL(file.file);
          }
          else{
            this.showSnackbarNotSupported();
          }
        })
      }
    },
  },
  methods: {
    confirmQuestions() {
      if (this.editableQuestions.length === 0) {
        this.showToast(this.$t('interview.noQuestionsToConfirm'), "warning");
        return;
      }

      this.questionsConfirmed = true;
      console.log("=== DEBUG CONFIRM QUESTIONS ===");
      console.log("questionsConfirmed:", this.questionsConfirmed);
      console.log("selectedCampaign:", this.selectedCampaign);
      console.log("selectedCheckboxes:", this.selectedCheckboxes);
      console.log("editableQuestions length:", this.editableQuestions.length);
      console.log("===============================");
      this.showToast(this.$t('interview.questionsConfirmedYouCanNowStartInterviews'), "success");

      // Force re-render
      this.$forceUpdate();
    },

    showToast(message, type = "error") {
      this.snackbarText = message;

      if (type === "error") {
        this.snackbarColor = "error";
        this.snackbarIcon = "mdi-alert-circle";
      } else if (type === "success") {
        this.snackbarColor = "success";
        this.snackbarIcon = "mdi-check-circle";
      } else if (type === "warning") {
        this.snackbarColor = "warning";
        this.snackbarIcon = "mdi-alert";
      } else if (type === "info") {
        this.snackbarColor = "info";
        this.snackbarIcon = "mdi-information";
      }

      this.showSnackbar = true;
    },

    toggleCheckAll() {
      if (this.allSelected) {
        this.selectedCheckboxes = [];
      } else {
        this.selectedCheckboxes = [...this.filteredPersonaOptions];
      }
    },

    async loadFilterPersonaSources(domain, clearFilters = true) {
      if (clearFilters) {
        this.onClearFilters();
      }

      // Overlaid, not raw static: custom attributes added via Attribute
      // Management only exist on the backend documents, and the filter panel
      // discovers its fields from whatever personas it is given.
      this.staticPersonasForFilter = await loadDomainPersonasWithOverlay(domain, this.t);
      this.generatedPersonasForFilter = await loadSavedPersonasForDomain(domain);
      this.schemaAttributes = await loadDomainSchemaAttributes(domain);
    },
    // Persona Schema for the filter panel (Option 2: schema-driven,
    // type-aware custom attribute filters). Failure/empty schema is
    // handled gracefully — schemaAttributes stays [] and
    // getAllFilterFields() falls back to its existing data-driven
    // discovery, exactly as before this feature existed.
    async loadSchemaAttributes(domain) {
      try {
        const response = await fetch_persona_domain_schema(domain);
        this.schemaAttributes = response.data?.attributes || [];
      } catch (error) {
        console.error('Failed to load persona domain schema for filters:', error);
        this.schemaAttributes = [];
      }
    },
    restoreActiveFilterState() {
      const saved = loadActiveFilterState(this.selectedDomain);
      if (!saved) return;
      const fields = getAllFilterFields(this.staticPersonasForFilter, false, this.schemaAttributes);
      const { criteria, droppedFields } = reconcileFilterCriteria(
        saved.criteria || [],
        fields,
        this.schemaAttributes,
      );
      this.activeFilterCriteria = criteria;
      if (droppedFields.length) {
        this.showToast(formatDroppedFilterFieldsMessage(droppedFields, this.t), 'warning');
        saveActiveFilterState(this.selectedDomain, {
          criteria,
          isGeneratedView: false,
        });
      }
    },
    onApplyFilters(criteria) {
      this.activeFilterCriteria = criteria;
      saveActiveFilterState(this.selectedDomain, {
        criteria,
        isGeneratedView: false,
      });
      this.ensureSelectedPersonasStillVisible();
    },
    onClearFilters() {
      this.activeFilterCriteria = [];
      clearActiveFilterState(this.selectedDomain);
    },
    ensureSelectedPersonasStillVisible() {
      const visibleIds = new Set(
        this.filteredPersonaOptions.map((persona) => persona.persona_id),
      );
      this.selectedCheckboxes = this.selectedCheckboxes.filter(
        (persona) => visibleIds.has(persona.persona_id),
      );
    },

    openDialog() {
      this.isAddDialogVisible = true;
    },

    async fetchCampaigns(selected_domain) {
      this.isCampaignsLoading = true;
      try {
        const response = await fetch_campaigns(selected_domain);
        if (response && Array.isArray(response.data)) {
          this.campaigns = response.data;
          console.log("Fetched campaigns:", this.campaigns);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        this.campaigns = [];
        this.showToast(this.$t('interview.failedToLoadTopicsPleaseTryAgain'), "error");
      } finally {
        this.isCampaignsLoading = false;
      }
    },

    async fetchPersonaNames(selected_domain) {
      try {
        const response = await fetch_persona_names(selected_domain);
        if (response && Array.isArray(response.data)) {
          this.personaOptions = response.data;
          this.ensureSelectedPersonasStillVisible();
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching personas:", error);
        this.personaOptions = [];
        this.showToast(this.$t('interview.failedToLoadPersonasPleaseTryAgain'), "error");
      }
    },

    async addCampaign(campaignData) {
      try {
        console.log("Submitting campaign:", campaignData);

        const response = await add_campaign(
            campaignData.campaign_name,
            campaignData.description,
            this.selectedDomain
        );

        console.log("Topic added successfully:", response.data);
        this.showToast(this.$t('interview.topicAddedSuccessfully'), "success");

        await this.fetchCampaigns(this.selectedDomain);
      } catch (error) {
        console.error("Error adding campaign:", error);
        this.showToast(this.$t('interview.failedToAddTopicPleaseTryAgain'), "error");
      }
    },
    promptDeleteTopic() {
      const campaign = this.campaigns.find(c => c.topic_name === this.selectedCampaign);
      if (!campaign) return;
      this.topicToDelete = campaign;
      this.showDeleteTopicConfirm = true;
    },

    async confirmDeleteTopic() {
      if (!this.topicToDelete) return;
      this.deletingTopic = true;
      try {
        await delete_campaign(this.topicToDelete.topic_id);
        this.campaigns = this.campaigns.filter(c => c.topic_id !== this.topicToDelete.topic_id);
        if (this.selectedCampaign === this.topicToDelete.topic_name) {
          this.selectedCampaign = null;
          this.editableQuestions = [];
          this.questionsConfirmed = false;
        }
        this.showToast(this.$t('interview.topicDeletedSuccessfully'), "success");
      } catch (error) {
        console.error("Error deleting topic:", error);
        this.showToast(this.$t('interview.failedToDeleteTopic'), "error");
      } finally {
        this.deletingTopic = false;
        this.showDeleteTopicConfirm = false;
        this.topicToDelete = null;
      }
    },

    async generateQuestions() {
      if (!this.selectedCampaign) {
        this.showToast(this.$t('interview.pleaseSelectATopicFirst'), "warning");
        return;
      }
      if (!this.selectedQuestionGenerationPrompt) {
        this.showToast(this.$t('interview.pleaseSelectAQuestionGenerationPromptFirst'), "warning");
        return;
      }

      this.isLoading_questions = true;
      this.questionsConfirmed = false;
      try {
        const campaign = this.campaigns.find(
            (campaign) => campaign.topic_name === this.selectedCampaign
        );

        let formData = new FormData();
        formData.append("campaign_name", campaign.topic_name);
        formData.append("query", campaign ? campaign.description : "");
        formData.append("campaign_id", campaign ? campaign.topic_id : "");
        formData.append("domain", this.selectedDomain);

        if (this.selectedQuestionGenerationPrompt) {
          formData.append("prompt_id", this.selectedQuestionGenerationPrompt);
        }

        const filesToSend = this.uploaded_files.slice(0, 5);
        for (let i = 0; i < filesToSend.length; i++) {
          formData.append("files", filesToSend[i].file, filesToSend[i].name);
        }

        console.log(
          "[LANG-DIAG] generateQuestions: localStorage.appLanguage=%o $i18n.locale=%o query=%o",
          localStorage.getItem('appLanguage'),
          this.$i18n.locale,
          (campaign ? campaign.description : "").slice(0, 40),
        );

        const response = await generate_interview_questions(formData);

        if (response.data && response.data.questions) {
          console.log(
            "[LANG-DIAG] generateQuestions RESPONSE: first_question=%o",
            response.data.questions[0] && response.data.questions[0].question,
          );
          console.log("Questions generated successfully:", response.data);
          this.questionFileMappingFromServer = response.data.file_id_mapping || {};
          const freshQuestions = response.data.questions.map((q) => ({
            text: q.question,
            file_id: q.file_id || null,
            isEditing: false,
          }));
          // A fresh generation invalidates any previously cached translation —
          // this is a new question set, not a re-display of the old one. Bump
          // the token so a translation still in flight for the OLD question
          // set (started before this click) discards its result instead of
          // caching/displaying it once it resolves.
          const generationLanguage = this.resolveCurrentLanguage();
          this.questionsByLanguage = { [generationLanguage]: freshQuestions };
          this.questionsGenerationToken += 1;
          const tokenAtStart = this.questionsGenerationToken;
          const otherLanguage = generationLanguage === 'ja' ? 'en' : 'ja';

          // Prepare the OTHER language now, BEFORE showing anything as ready
          // -- awaited, not fire-and-forget, so both languages are cached by
          // the time the questions are considered "generated" and switching
          // is instant from the very first switch. Reuses syncQuestionsToLanguage
          // as-is: same cache, same generation-token and in-flight guards as
          // an on-demand switch, so there's no separate logic to keep in sync.
          // On failure it's caught internally (never rejects) and shows its
          // own error toast (isInitialPrep: true) -- generation still
          // succeeds below with the original-language questions.
          await this.syncQuestionsToLanguage(otherLanguage, { isInitialPrep: true });

          // A newer generation (or topic change) may have superseded this one
          // while we were preparing the other language -- if so, its own
          // completion already owns editableQuestions/questionsByLanguage;
          // applying this (now stale) generation's result would clobber that.
          if (this.questionsGenerationToken !== tokenAtStart) return;

          this.editableQuestions = this.questionsByLanguage[generationLanguage];
          this.showToast(this.$t('interview.questionsGeneratedSuccessfully'), "success");
        }
      } catch (error) {
        if (error.response) {
          console.error("Validation Error:", error.response.data);
          this.showToast(`Validation Error: ${error.response.data.detail}`, "error");
        } else {
          console.error("Error during question generation:", error);
          this.showToast(this.$t('interview.failedToGenerateQuestionsPleaseTryAgain'), "error");
        }
      } finally {
        this.isLoading_questions = false;
      }
    },

    // Mirrors the backend's normalize_language: anything not starting with
    // "ja" falls back to "en". Keeps the cache keys consistent regardless of
    // exactly how $i18n.locale is spelled ('ja', 'ja-JP', etc.).
    resolveCurrentLanguage() {
      return String(this.$i18n.locale || '').toLowerCase().startsWith('ja') ? 'ja' : 'en';
    },

    // Switches the displayed question set to `targetLanguage`, translating
    // on demand (once) and caching the result so switching back and forth
    // never re-translates or re-calls the LLM for a language already seen.
    //
    // Also doubles as the "prepare the other language before showing
    // anything as ready" step, called with `isInitialPrep: true` from
    // generateQuestions() -- same cache, same token/in-flight guards, same
    // translation call, just awaited before display instead of fired
    // afterward. isInitialPrep only changes whether a failure notifies the
    // user (see the catch block): during initial prep the user is actively
    // waiting on question generation to finish, so a failure there is
    // directly relevant, unlike a silent post-display background retry.
    async syncQuestionsToLanguage(targetLanguage, { isInitialPrep = false } = {}) {
      // Checked against questionsByLanguage, not editableQuestions: during
      // initial prep, questionsByLanguage already holds the native-language
      // seed but editableQuestions hasn't been set yet (that's the point).
      if (Object.keys(this.questionsByLanguage).length === 0) return; // nothing generated yet
      if (this.questionsByLanguage[targetLanguage]) {
        if (this.resolveCurrentLanguage() === targetLanguage) {
          this.editableQuestions = this.questionsByLanguage[targetLanguage];
        }
        return;
      }
      if (this.isTranslatingQuestions && this.translatingToLanguage === targetLanguage) {
        return; // a translation to this exact language is already in flight
      }

      // Translate from whatever language we already have natively (the
      // original generation), never from a previous translation, to avoid
      // compounding translation drift across repeated switches.
      const sourceLanguage = Object.keys(this.questionsByLanguage)[0];
      const sourceQuestions = this.questionsByLanguage[sourceLanguage];
      if (!sourceQuestions || sourceQuestions.length === 0) return;

      // Snapshot which generation this translation belongs to. If a fresh
      // Generate click (or a topic change) happens while this request is in
      // flight, the token changes and the result below is discarded -- it
      // belongs to a question set that no longer exists on screen.
      const tokenAtStart = this.questionsGenerationToken;
      this.isTranslatingQuestions = true;
      this.translatingToLanguage = targetLanguage;
      try {
        const payloadQuestions = sourceQuestions.map((q) => ({
          question: q.text,
          file_id: q.file_id || null,
        }));
        const response = await translate_interview_questions(payloadQuestions, targetLanguage);

        if (this.questionsGenerationToken !== tokenAtStart) {
          return; // stale: a newer question set has replaced this one
        }

        const translated = (response.data.questions || []).map((q, idx) => ({
          text: q.question,
          file_id: q.file_id || sourceQuestions[idx]?.file_id || null,
          isEditing: false,
        }));
        if (translated.length !== sourceQuestions.length) {
          throw new Error('translated question count mismatch');
        }
        this.questionsByLanguage = { ...this.questionsByLanguage, [targetLanguage]: translated };
        // Only apply if the user is still on the language we just translated
        // to (they may have switched again while the request was in flight).
        if (this.resolveCurrentLanguage() === targetLanguage) {
          this.editableQuestions = translated;
        }
      } catch (error) {
        console.error("Error translating questions:", error);
        // Surface the error toast when it's relevant to what the user is
        // actually experiencing right now:
        // - isInitialPrep: the user is actively waiting on question
        //   generation to finish (nothing is displayed yet), so a failure to
        //   prepare the other language is directly relevant even though
        //   they haven't switched -- generation still succeeds and shows the
        //   original-language questions (handled by the caller), but they
        //   should know the other language isn't available yet.
        // - otherwise: only if they're currently on the language that just
        //   failed to translate (an active switch, or waiting on an
        //   already-in-flight one) -- a background retry for a language
        //   they're not viewing fails silently, same as before.
        // Either way editableQuestions is never touched on this path, so the
        // last valid questions (or nothing yet, during initial prep) stay
        // exactly as they were.
        if (this.questionsGenerationToken === tokenAtStart
            && (isInitialPrep || this.resolveCurrentLanguage() === targetLanguage)) {
          this.showToast(this.$t('interview.failedToTranslateQuestionsShowingOriginal'), "warning");
        }
      } finally {
        this.isTranslatingQuestions = false;
        this.translatingToLanguage = null;
      }
    },

    async startInterview() {
      if (!this.selectedCampaign) {
        this.showToast(this.$t('interview.pleaseSelectATopicFirst'), "warning");
        return;
      }
      if (this.selectedCheckboxes.length === 0) {
        this.showToast(this.$t('interview.pleaseSelectAtLeastOnePersona'), "warning");
        return;
      }
      if (!this.questionsConfirmed) {
        this.showToast(this.$t('interview.pleaseConfirmQuestionsFirst'), "warning");
        return;
      }

      this.isLoading_interview = true;
      try {
        const campaign = this.campaigns.find(
            (campaign) => campaign.topic_name === this.selectedCampaign
        );

        let formData = new FormData();
        formData.append("campaign_name", this.selectedCampaign);
        formData.append("query", campaign ? campaign.description : "");
        formData.append("domain", this.selectedDomain);

        this.selectedCheckboxes.forEach(persona => {
          formData.append("personas_list", persona.persona_id);
        });

        formData.append("campaign_id", campaign.topic_id);

        const questionsArray = this.editableQuestions.map(q => ({
          question_text: q.text,
          file_ids: q.file_id ? [q.file_id] : []
        }));

        const questionsDataPayload = {
          questions: questionsArray
        };
        formData.append("questions_data", JSON.stringify(questionsDataPayload));

        const filesWithIds = this.uploaded_files.filter(file =>
            this.editableQuestions.some(q => q.file_id === file.name)
        );

        if (filesWithIds.length > 0) {
          const fileIds = filesWithIds.map(f => f.name);
          formData.append("file_ids", JSON.stringify(fileIds));

          filesWithIds.forEach(file => {
            formData.append("files", file.file, file.name);
          });
        }

        console.log("Interview payload being sent");

        await start_interview(formData);

        this.backgroundRunStatus = 'running';
        this.backgroundRunError = null;
        this.showToast(this.$t('interview.interviewStartedRunningInBackground'), "info");
        this.startPolling();

      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.showToast(this.$t('interview.anotherRunIsAlreadyInProgressPleaseWait'), "warning");
        } else if (error.response) {
          console.error("Validation Error:", error.response.data);
          this.showToast(`Validation Error: ${error.response.data.detail}`, "error");
        } else {
          console.error("Error during start interview:", error);
          this.showToast(this.$t('interview.failedToStartTheInterviewPleaseTryAgain'), "error");
        }
      } finally {
        this.isLoading_interview = false;
      }
    },

    async startFocusGroup() {
      if (!this.selectedCampaign) {
        this.showToast(this.$t('interview.pleaseSelectATopicFirst'), "warning");
        return;
      }
      if (this.selectedCheckboxes.length === 0) {
        this.showToast(this.$t('interview.pleaseSelectAtLeastOnePersona'), "warning");
        return;
      }
      // Generate Questions is disabled in template mode, so this guard can never
      // be satisfied there — the template's steps carry the structure instead.
      if (this.executionMode !== 'template_based' && !this.questionsConfirmed) {
        this.showToast(this.$t('interview.pleaseConfirmQuestionsFirst'), "warning");
        return;
      }
      if (this.executionMode === 'template_based') {
        if (!this.selectedMeetingTemplate) {
          this.showToast(this.$t('interview.pleaseSelectAMeetingTemplateFirst'), "warning");
          return;
        }
        if (!this.selectedTemplateSummarizePrompt) {
          this.showToast(this.$t('interview.pleaseSelectASummarizePromptFirst'), "warning");
          return;
        }
        if (!this.selectedTemplateImprovementPlanPrompt) {
          this.showToast(this.$t('interview.pleaseSelectAnImprovementPlanPromptFirst'), "warning");
          return;
        }
        if (this.templateAttachmentsBlocker) {
          this.showToast(this.templateAttachmentsBlockerMessage, "warning");
          return;
        }
      } else {
        if (!this.selectedSummarizePrompt) {
          this.showToast(this.$t('interview.pleaseSelectASummarizePromptFirst'), "warning");
          return;
        }
        if (!this.selectedImprovementPlanPrompt) {
          this.showToast(this.$t('interview.pleaseSelectAnImprovementPlanPromptFirst'), "warning");
          return;
        }
      }

      this.isLoading_fg = true;
      this.attachmentStartError = null;
      try {
        const campaign = this.campaigns.find(
            (campaign) => campaign.topic_name === this.selectedCampaign
        );

        if (this.executionMode === 'template_based') {
          // No domain (derived from the template), no questions and no files:
          // the template's steps carry the whole structure of the run, and the
          // stimulus materials, if any, are referenced by file_id in list order.
          const payload = buildTemplateFocusGroupPayload({
            campaign,
            campaignName: this.selectedCampaign,
            templateId: this.selectedMeetingTemplate,
            personaIds: this.selectedCheckboxes.map(persona => persona.persona_id),
            summarizerPromptId: this.selectedTemplateSummarizePrompt,
            improvementPlanPromptId: this.selectedTemplateImprovementPlanPrompt,
            attachments: this.templateAttachments,
          });

          console.log("Template focus group payload being sent");

          await start_template_focus_group(payload);
        } else {
          let formData = new FormData();
          formData.append("campaign_name", this.selectedCampaign);
          formData.append("query", campaign ? campaign.description : "");
          formData.append("domain", this.selectedDomain);

          this.selectedCheckboxes.forEach(persona => {
            formData.append("persona_ids", persona.persona_id);
          });

          formData.append("campaign_id", campaign.topic_id);

          const questionsArray = this.editableQuestions.map(q => ({
            question_text: q.text,
            image_ids: q.file_id ? [q.file_id] : []
          }));

          formData.append("questions_data", JSON.stringify(questionsArray));

          if (this.selectedSummarizePrompt) {
            formData.append("summarizer_prompt_id", this.selectedSummarizePrompt);
          }
          if (this.selectedImprovementPlanPrompt) {
            formData.append("improvement_plan_prompt_id", this.selectedImprovementPlanPrompt);
          }

          if (this.uploaded_files.length > 0) {
            const fileIds = Object.keys(this.questionFileMappingFromServer);
            formData.append("file_ids", JSON.stringify(fileIds));

            this.uploaded_files.forEach(file => {
              formData.append("files", file.file, file.name);
            });
          }

          console.log("Focus group payload being sent");
          console.log(
            "[LANG-DIAG] startFocusGroup: localStorage.appLanguage=%o $i18n.locale=%o",
            localStorage.getItem('appLanguage'),
            this.$i18n.locale,
          );

          await start_focus_group(formData);
        }

        this.backgroundRunStatus = 'running';
        this.backgroundRunError = null;
        this.showToast(this.$t('interview.focusGroupStartedRunningInBackground'), "info");
        this.startPolling();

      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;

        if (status === 409) {
          this.showToast(this.$t('interview.anotherRunIsAlreadyInProgressPleaseWait'), "warning");
        } else if (typeof detail === 'string' && detail) {
          // 400 / 404 / 503 bodies are written to be user-readable (template has
          // no steps, unrunnable step, template, persona or file not found, file
          // of another domain or without pages, too many pages in total, storage
          // disabled, ...), so show them verbatim. One naming listed files by
          // file_id also points at them in the list.
          console.error(`Start focus group failed (${status}):`, detail);
          this.showToast(detail, "error");
          if (this.executionMode === 'template_based') {
            const fileIds = attachmentsNamedIn(detail, this.templateAttachments);
            this.attachmentStartError = fileIds.length ? { fileIds, detail } : null;
          }
        } else {
          // 422 carries a list in `detail`, not text; network errors carry none.
          console.error("Error during start focus group:", error);
          this.showToast(this.$t('interview.failedToStartTheInterviewPleaseTryAgain'), "error");
        }
      } finally {
        this.isLoading_fg = false;
      }
    },


    // Runs are tracked per domain: only a run of the selected domain may disable
    // the start buttons.
    async checkBackgroundRunStatus() {
      const domain = this.selectedDomain;
      this.stopPolling();
      this.backgroundRunStatus = 'idle';
      try {
        const res = await get_background_run_status(domain);
        if (domain === this.selectedDomain && res.data.status === 'running') {
          this.backgroundRunStatus = 'running';
          this.startPolling();
        }
      } catch (err) {
        console.error("Error checking background run status:", err);
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollingInterval = setInterval(async () => {
        const domain = this.selectedDomain;
        try {
          const res = await get_background_run_status(domain);
          // The user switched domain while this request was in flight.
          if (domain !== this.selectedDomain) return;
          this.backgroundRunStatus = res.data.status;
          if (res.data.status === 'completed') {
            this.stopPolling();
            this.showToast(this.$t('interview.runCompletedSuccessfully'), "success");
            await this.fetchFocusGroups(this.selectedDomain);
          } else if (res.data.status === 'failed') {
            this.stopPolling();
            this.backgroundRunError = res.data.error || 'Unknown error';
            this.showToast(`${this.$t('interview.runFailed')} : ${this.backgroundRunError}`, "error");
          } else if (res.data.status === 'idle') {
            // Nothing tracked any more (e.g. the backend restarted): stop asking.
            this.stopPolling();
          }
        } catch (err) {
          console.error('Error polling background run status:', err);
        }
      }, 7000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async fetchFocusGroups(domain_name) {
      try {
        const response = await fetch_focus_groups(domain_name);
        if (response && Array.isArray(response.data)) {
          this.focusGroups = response.data;
          console.log("Fetched focus groups:", this.focusGroups);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching focus groups:", error);
        this.focusGroups = [];
      }
    },
    pickAttachments() {
      this.$refs.attachmentInput?.click();
    },

    onAttachmentsPicked(event) {
      const files = Array.from(event.target.files || []);
      // Cleared so that picking the same files again still fires `change`.
      event.target.value = '';
      if (files.length && this.canAddStimulusMaterials) {
        this.addTemplateAttachments(files);
      }
    },

    // Each file is uploaded as soon as it is added, one call per file and all in
    // parallel, for the selected template's domain: the start request then only
    // carries the file_ids. A file already in the list is skipped.
    addTemplateAttachments(files) {
      const domain = this.selectedTemplate.domain_id;
      const listed = new Set(this.templateAttachments.map(item => item.source));
      const skipped = [];
      const added = [];
      files.forEach((file) => {
        const source = localFileSignature(file);
        if (listed.has(source)) {
          skipped.push(file.name);
          return;
        }
        listed.add(source);
        added.push(uploadingAttachment(file, `attachment-${++this.attachmentKeySeq}`, domain));
      });

      this.attachmentNotice = skipped.length
          ? this.$t('interview.stimulusMaterialsAlreadyAdded', { names: skipped.join(', ') })
          : '';
      this.templateAttachments = [...this.templateAttachments, ...added];
      added.forEach(item => this.uploadTemplateAttachment(item.key));
    },

    findTemplateAttachment(key) {
      return this.templateAttachments.find(item => item.key === key);
    },

    // Uploads, or retries, one listed file; the list may change meanwhile.
    uploadTemplateAttachment(key) {
      return uploadListedAttachment(key, {
        read: () => this.templateAttachments,
        write: (items) => { this.templateAttachments = items; },
        upload: upload_file,
        messages: {
          tooLarge: this.$t('interview.stimulusMaterialTooLarge', { max: MAX_UPLOAD_MB }),
          uploadFailed: this.$t('interview.stimulusMaterialUploadFailed'),
        },
        onError: (error) => console.error("Error uploading stimulus material:", error),
      });
    },

    // Only takes the file off the list: there is nothing to delete server-side.
    removeTemplateAttachment(key) {
      this.findTemplateAttachment(key)?.controller?.abort();
      this.templateAttachments = this.templateAttachments.filter(item => item.key !== key);
    },

    clearTemplateAttachments() {
      this.templateAttachments.forEach(item => item.controller?.abort());
      this.templateAttachments = [];
      this.attachmentStartError = null;
    },

    discardTemplateAttachments() {
      this.clearTemplateAttachments();
      this.attachmentNotice = this.$t('interview.stimulusMaterialsRemovedOtherDomain');
      this.showToast(this.attachmentNotice, "warning");
    },

    removeUploadedFiles(){
      this.uploaded_files = [];
      this.uploaded_image_url = null;
      this.questionsConfirmed = false;
    },
    removeFileFromUploadedFiles(index){
      this.uploaded_files.splice(index, 1);
      this.questionsConfirmed = false;
    },
    showSnackbarDownloaded(){
      this.snackbar_downloaded = false;
      this.$nextTick(() => {
        this.snackbar_downloaded = true;
      });
    },
    showDownloadingSpinner(){
      this.downloading_spinner = true;
    },
    stopDownloadingSpinner(){
      this.downloading_spinner = false;
    },
    getFileExt(filename){
      var re = /(?:\.([^.]+))?$/;
      return '.' + re.exec(filename)[1];
    },
    async fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    decodeBase64(content) {
      if(!content) return  null;

      try {
        let byteCharacters = ""
        try {
          const base64Content = content.split(',')[1];
          byteCharacters = atob(base64Content);
        } catch (InvalidCharacterError) {
          byteCharacters = atob(content)
        }

        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], {type: 'application/pdf'});

      }catch(Error){
        console.log("Error while decoding file");
        return null
      }
    },
    performDownload(content, filename){
      if(!content || !filename) return

      const blob = this.decodeBase64(content);
      if(!blob) return

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      this.stopDownloadingSpinner();
      this.showSnackbarDownloaded();
    },
    async downloadMedia(content, filename) {
      if(!content || !filename) return
      this.showDownloadingSpinner();
      if(content instanceof File){
        const base64 = await this.fileToBase64(content);
        if(base64) this.downloadMedia(base64, filename);
        else this.stopDownloadingSpinner();
      } else {
        this.performDownload(content,filename);
      }
    },
    showSnackbarNotSupported() {
      this.snackbar_not_supported = false;
      this.$nextTick(() => {
        this.snackbar_not_supported = true;
      });
    },

    async fetchAIPrompts() {
      this.isLoadingAIPrompts = true;
      try {
        const currentUser = get_current_user_from_storage();
        const groupId = currentUser?.group_ids?.[0] ?? null;

        const response = await fetch_prompts({
          domain: this.selectedDomain,
          execution_mode: 'question_driven',
          group_id: groupId,
        });

        const data = response.data;
        this.aiPrompts = Array.isArray(data) ? data : (data?.items ?? data?.prompts ?? []);

        // Reset selections if the previously selected prompt is no longer in the list
        if (!this.aiPrompts.some(p => p.prompt_id === this.selectedQuestionGenerationPrompt)) {
          this.selectedQuestionGenerationPrompt = null;
        }
        if (!this.aiPrompts.some(p => p.prompt_id === this.selectedSummarizePrompt)) {
          this.selectedSummarizePrompt = null;
        }
        if (!this.aiPrompts.some(p => p.prompt_id === this.selectedImprovementPlanPrompt)) {
          this.selectedImprovementPlanPrompt = null;
        }
      } catch (error) {
        console.error("Error fetching AI prompts:", error);
        this.aiPrompts = [];
      } finally {
        this.isLoadingAIPrompts = false;
      }
    },

    async fetchMeetingTemplates() {
      this.isLoadingMeetingTemplates = true;
      try {
        const currentUser = get_current_user_from_storage();
        const groupId = currentUser?.group_ids?.[0] ?? null;

        const response = await fetch_meeting_templates({
          domain: this.selectedDomain,
          group_id: groupId,
        });

        this.meetingTemplates = Array.isArray(response.data) ? response.data : (response.data?.items ?? []);

        if (!this.meetingTemplates.some(t => t.template_id === this.selectedMeetingTemplate)) {
          this.selectedMeetingTemplate = null;
        }
      } catch (error) {
        console.error("Error fetching meeting templates:", error);
        this.meetingTemplates = [];
      } finally {
        this.isLoadingMeetingTemplates = false;
      }
    },

    async fetchTemplatePrompts() {
      this.isLoadingTemplatePrompts = true;
      try {
        const currentUser = get_current_user_from_storage();
        const groupId = currentUser?.group_ids?.[0] ?? null;

        const response = await fetch_prompts({
          domain: this.selectedDomain,
          execution_mode: 'template_based',
          group_id: groupId,
        });

        const data = response.data;
        this.templatePrompts = Array.isArray(data) ? data : (data?.items ?? data?.prompts ?? []);

        if (!this.templatePrompts.some(p => p.prompt_id === this.selectedTemplateSummarizePrompt)) {
          this.selectedTemplateSummarizePrompt = null;
        }
        if (!this.templatePrompts.some(p => p.prompt_id === this.selectedTemplateImprovementPlanPrompt)) {
          this.selectedTemplateImprovementPlanPrompt = null;
        }
      } catch (error) {
        console.error("Error fetching template-based prompts:", error);
        this.templatePrompts = [];
      } finally {
        this.isLoadingTemplatePrompts = false;
      }
    }
  },

  async created() {
    try {
      await this.loadFilterPersonaSources(this.selectedDomain, false);
      this.restoreActiveFilterState();
      await this.fetchCampaigns(this.selectedDomain);
      await this.fetchPersonaNames(this.selectedDomain);
      await this.fetchAIPrompts();
    } catch (error) {
      console.error("Error initializing component:", error);
      this.showToast(this.$t('interview.failedToLoadInitialDataPleaseRefreshThePage'), "error");
    }

    await this.checkBackgroundRunStatus();
  },

  beforeUnmount() {
    this.stopPolling();
    this.clearTemplateAttachments();
  },
};
</script>
<style scoped>
::v-deep label {
  cursor: pointer;
}

.file-uploads {
  display: inline;
}

.align-stretch {
  align-items: stretch;
}

.h-100 {
  height: 100%;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}

.topic-description {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);
  padding: 8px 4px;
}

.generated-questions {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);
  padding: 8px 4px;
}

.persona-chips .v-chip--selected {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-primary-base), 0.1) !important;
}

.v-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}

.v-radio-group :deep(.v-selection-control-group) {
  align-items: stretch !important;
}

.ai-prompt-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: auto;
  column-gap: 24px;
  row-gap: 0;
}

@media (max-width: 959px) {
  .ai-prompt-grid {
    grid-template-columns: 1fr;
  }
}

.ai-prompt-select {
  width: 100%;
}

.ai-prompt-select :deep(.v-field) {
  height: 56px !important;
  min-height: 56px !important;
  max-height: 56px !important;
}

.ai-prompt-select :deep(.v-field__input) {
  height: 56px !important;
  min-height: 56px !important;
  max-height: 56px !important;
  flex-wrap: nowrap !important;
  overflow: hidden !important;
}

.ai-prompt-select :deep(.v-select__selection) {
  max-width: 100%;
}

.ai-prompt-select :deep(.v-select__selection-text) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.ai-prompt-select :deep(.v-field__input) {
  align-items: center;
  display: flex;
}

.ai-prompt-select :deep(.v-select__selection) {
  align-items: center;
  display: flex;
}

.ai-prompt-select :deep(input),
.ai-prompt-select :deep(.v-field__input > input) {
  align-self: center;
}

.ai-prompt-select :deep(.v-field__input) > * {
  align-self: center;
}
</style>
